import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'motion/react';
import brand, { otherBrand } from '../brand';

const menuItems = [
  { to: '/about', label: 'About' },
  { to: '/rules', label: 'Rules' },
  { to: '/faq', label: 'FAQ' },
  { to: '/partners', label: 'Partners' },
  { to: '/events', label: 'Events' },
  { to: '/blog', label: 'Blog' },
  { href: brand.membersAreaUrl, label: 'Members Area', external: true },
];

// Shared between the inline menu and the dropdown, so both stay visually
// and behaviourally consistent. Internal links use NavLink so the current
// page gets a distinct "you are here" style, not just a hover state.
function MenuLink({ item, onNavigate }) {
  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="nav-link"
        onClick={onNavigate}
      >
        {item.label}
        <span className="nav-external-icon" aria-hidden="true">↗</span>
      </a>
    );
  }
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => `nav-link${isActive ? ' nav-link-active' : ''}`}
      onClick={onNavigate}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="nav-active-pill"
              className="nav-active-pill"
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            />
          )}
          <span className="nav-link-text">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

// Inline menu for when full header fits
function InlineMenu() {
  return (
    <nav className="nav-menu" aria-label="Primary">
      <ul>
        {menuItems.map((item) => (
          <li key={item.label}>
            <MenuLink item={item} />
          </li>
        ))}
      </ul>
    </nav>
  );
}

// Hamburger dropdown menu for when space is limited
function HamburgerMenu({ dropdownVisible, toggleDropdown, closeDropdown }) {
  // Close on Escape, for keyboard users
  useEffect(() => {
    if (!dropdownVisible) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeDropdown();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dropdownVisible, closeDropdown]);

  return (
    <div className="hamburger-menu">
      <button
        type="button"
        className={`hamburger-icon${dropdownVisible ? ' open' : ''}`}
        onClick={toggleDropdown}
        aria-expanded={dropdownVisible}
        aria-controls="primary-dropdown-menu"
        aria-label={dropdownVisible ? 'Close menu' : 'Open menu'}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      <nav
        id="primary-dropdown-menu"
        className={`dropdown${dropdownVisible ? ' open' : ''}`}
        aria-label="Primary"
      >
        <ul>
          {menuItems.map((item) => (
            <li key={item.label}>
              <MenuLink item={item} onNavigate={closeDropdown} />
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

const Header = () => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [useHamburger, setUseHamburger] = useState(false);
  const headerRef = useRef(null);

  const toggleDropdown = () => setDropdownVisible(prev => !prev);
  const closeDropdown = () => setDropdownVisible(false);

  // Header is mounted fresh on every route change (each page renders its
  // own <Header/>), so `useHamburger` always starts back at its `false`
  // default even on a narrow viewport. Left uncorrected until the debounced
  // ResizeObserver below caught up, that showed up as a visible flash of
  // the inline menu right after navigating from the hamburger dropdown.
  // useLayoutEffect runs before the browser paints, so this correction
  // lands before the wrong state is ever shown on screen.
  useLayoutEffect(() => {
    if (!headerRef.current) return;
    const gap = headerRef.current.clientWidth - headerRef.current.scrollWidth;
    if (gap < 0) setUseHamburger(true);
  }, []);

  useEffect(() => {
    let debounceTimeout;
    const hysteresisMargin = 20;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        if (!headerRef.current) return;
        const gap = headerRef.current.clientWidth - headerRef.current.scrollWidth;
        if (!useHamburger && gap < 0) {
          setUseHamburger(true);
        } else if (useHamburger && gap > hysteresisMargin) {
          setUseHamburger(false);
          setDropdownVisible(false);
        }
      }, 150);
    });
    if (headerRef.current) observer.observe(headerRef.current);
    return () => {
      clearTimeout(debounceTimeout);
      observer.disconnect();
    };
  }, [useHamburger]);

  return (
    <>
      <header className="navbar" ref={headerRef}>
        <div className="logo-title">
          <Link to="/" className="logo-link">
            <img
              src={brand.logo}
              alt={brand.logoAlt}
              loading="lazy"
              className="logo-img"
            />
          </Link>
          <span className="site-title">{brand.siteName}</span>
          <a
            href={brand.otherDomain}
            className="brand-switch"
            aria-label={`Switch to ${otherBrand.siteName}`}
            title={`Switch to ${otherBrand.siteName}`}
          >
            <img
              src={otherBrand.logo}
              alt=""
              className="brand-switch-logo"
              loading="lazy"
            />
            <span className="brand-switch-label">{otherBrand.shortName}</span>
          </a>
        </div>
        {useHamburger ? (
          <HamburgerMenu
            dropdownVisible={dropdownVisible}
            toggleDropdown={toggleDropdown}
            closeDropdown={closeDropdown}
          />
        ) : (
          <InlineMenu />
        )}
      </header>
      {useHamburger && (
        <div
          className={`nav-backdrop${dropdownVisible ? ' open' : ''}`}
          onClick={closeDropdown}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Header;
